import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  updateDoc,
  addDoc,
  query,
  where,
  getDocs,
  collectionData,
  getDoc,
} from '@angular/fire/firestore';
import Swal from 'sweetalert2';
import { Simulation } from '../models/simulation';
import { combineLatest, from } from 'rxjs';
import { map } from 'rxjs/operators';

function normalizeSimulation(data: any): Simulation {
  return {
    ...data,
    ab_demandante: Array.isArray(data.ab_demandante)
      ? data.ab_demandante
      : data.ab_demandante ? [data.ab_demandante] : [],
    ab_defensor: Array.isArray(data.ab_defensor)
      ? data.ab_defensor
      : data.ab_defensor ? [data.ab_defensor] : [],
  };
}

@Injectable({
  providedIn: 'root',
})
export class SimulationService {
  private firestore: Firestore = inject(Firestore);

  getSimulations() {
    const collectionRef = collection(this.firestore, 'simulation');
    return (collectionData(collectionRef, { idField: 'id' }) as any).pipe(
      map((sims: any[]) => sims.map(normalizeSimulation))
    );
  }

  getSimulationByIdentifier(identifier: string) {
    const collectionRef = collection(this.firestore, 'simulation');
    const q = query(collectionRef, where('identifier', '==', identifier));
    return from(getDocs(q)).pipe(
      map((snapshot) =>
        snapshot.docs.map((docSnap) => {
          const data = { ...docSnap.data(), id: docSnap.id };
          return normalizeSimulation(data);
        })
      )
    );
  }

  watchSimulationByIdentifier(identifier: string) {
    const collectionRef = collection(this.firestore, 'simulation');
    const q = query(collectionRef, where('identifier', '==', identifier));
    return (collectionData(q, { idField: 'id' }) as any).pipe(
      map((simulations: any[]) => normalizeSimulation(simulations[0]))
    );
  }

  getSimulation(identifier: string) {
    const collectionRef = collection(this.firestore, 'simulation');
    const q = query(collectionRef, where('identifier', '==', identifier));
    return from(getDocs(q)).pipe(
      map((snapshot) => {
        const docSnap = snapshot.docs[0];
        const data = { ...docSnap.data(), id: docSnap.id };
        return normalizeSimulation(data);
      })
    );
  }

  getSimulationByUser(userName: string) {
    console.log('getSimulationByUser llamado con userName:', userName);
    const simulationRef = collection(this.firestore, 'simulation');

    const q1 = query(simulationRef, where('juez', '==', userName));
    const q2 = query(simulationRef, where('ab_demandante', 'array-contains', userName));
    const q3 = query(simulationRef, where('ab_defensor', 'array-contains', userName));
    const q4 = query(simulationRef, where('secretario', '==', userName));

    // Para datos legados (string), también buscar con ==
    const q2Legacy = query(simulationRef, where('ab_demandante', '==', userName));
    const q3Legacy = query(simulationRef, where('ab_defensor', '==', userName));

    const toNorm = (res: any) => res.docs.map((d: any) => normalizeSimulation({ ...d.data(), id: d.id }));

    const $juez = from(getDocs(q1)).pipe(map(toNorm));
    const $ab_demandante = from(getDocs(q2)).pipe(map(toNorm));
    const $ab_defensor = from(getDocs(q3)).pipe(map(toNorm));
    const $secretario = from(getDocs(q4)).pipe(map(toNorm));
    const $ab_demandante_legacy = from(getDocs(q2Legacy)).pipe(map(toNorm));
    const $ab_defensor_legacy = from(getDocs(q3Legacy)).pipe(map(toNorm));

    return combineLatest([$juez, $ab_demandante, $ab_defensor, $secretario, $ab_demandante_legacy, $ab_defensor_legacy]).pipe(
      map(([juez, ab_dem, ab_def, secretario, ab_dem_leg, ab_def_leg]) => {
        const all = [...juez, ...ab_dem, ...ab_def, ...secretario, ...ab_dem_leg, ...ab_def_leg];
        const unique = all.filter((sim, idx, arr) => arr.findIndex(s => s.id === sim.id) === idx);
        console.log('Total de simulaciones encontradas:', unique.length);
        return unique;
      })
    );
  }

  putSimulation(simulation: Simulation) {
    const docRef = doc(this.firestore, `simulation/${simulation.id}`);
    return from(updateDoc(docRef, { ...simulation }));
  }

  postSimulation(simulation: Simulation) {
    const collectionRef = collection(this.firestore, 'simulation');
    return from(addDoc(collectionRef, simulation)).subscribe({
      next: () =>
        Swal.fire({
          title: 'Simulación Creada',
          text: 'Simulación creada con éxito!',
          icon: 'success',
        }),
      error: (err) =>
        Swal.fire({
          title: 'Error',
          text: err,
          icon: 'error',
        }),
    });
  }
}
